import { UserProfile, Theme, ThemeAvailableDiscountProgramsEnum } from '@parkingbase/ecommerce-api-test';

/**
 * Check if a user has custom programs (anything that isn't VIP, WEBSITE, or MONTHLY).
 */
function hasCustomPrograms(enrolledNames: string[]): boolean {
  return enrolledNames.some((name) => {
    const upper = name.toUpperCase().trim();
    return (
      upper !== ThemeAvailableDiscountProgramsEnum.VIP &&
      upper !== ThemeAvailableDiscountProgramsEnum.WEBSITE &&
      upper !== ThemeAvailableDiscountProgramsEnum.MONTHLY
    );
  });
}

/**
 * Whether the tenant has loyalty switched on at all.
 *
 * ## Invariants:
 * - On a converge website, ANY company with `loyaltyEnabled` counts, not just the default theme.
 *   Why: one deployment serves many companies there, and the legacy app's own gate is
 *   `some(convergeThemes, x => x.loyaltyEnabled)` (`LeftNav.tsx`). Reading only the default
 *   theme hides loyalty from every company on the site whenever the hub company happens to have
 *   it off — which looks identical to the feature simply not being configured.
 * - `companyThemes` is optional so the pre-existing two-argument call sites keep their exact
 *   behaviour: with no map to consult there is no converge information, and the default theme is
 *   the only thing that can be checked.
 */
function isLoyaltyEnabledForTenant(
  theme: Theme | null | undefined,
  companyThemes?: Record<string, Theme> | null
): boolean {
  if (theme?.isConvergeWebsite && companyThemes) {
    return Object.values(companyThemes).some((companyTheme) => !!companyTheme?.loyaltyEnabled);
  }
  return !!theme?.loyaltyEnabled;
}

/**
 * Determines whether the Loyalty nav item should be visible.
 * Mirrors the legacy app's `isAvailableDiscount` + `loyaltyEnabled` check.
 *
 * Returns true when:
 * 1. loyalty is enabled for the tenant (see `isLoyaltyEnabledForTenant` for the converge rule)
 * 2. user.disableDiscounts is not true
 * 3. user has at least one enrolled discount program that matches theme.availableDiscountPrograms
 *
 * ## Invariants:
 * - `companyThemes` is OPTIONAL and omitting it preserves the original two-argument behaviour
 *   exactly. Why: existing callers (both layouts) pass two arguments, and the converge widening
 *   must not change what they resolve to.
 * - A guest resolves to false, deliberately diverging from the legacy `isAvailableDiscount`,
 *   which returned true whenever no profile was loaded. Why: that default existed so a nav item
 *   didn't flicker before hydration, whereas every consumer here gates real content on program
 *   membership — and a signed-out visitor cannot be enrolled in one.
 */
export function isLoyaltyVisible(
  user: UserProfile | null | undefined,
  theme: Theme | null | undefined,
  companyThemes?: Record<string, Theme> | null
): boolean {
  if (!isLoyaltyEnabledForTenant(theme, companyThemes)) return false;
  if (user?.disableDiscounts) return false;
  // Explicit, because the enabled check above can now pass on a converge site via companyThemes
  // while the default theme itself is null — the membership check below still needs its companyId.
  if (!theme) return false;

  const companyId = theme.companyId;
  if (!companyId || !user?.discountProgramMap) return false;

  const enrolled = user.discountProgramMap[companyId];
  if (!enrolled?.length) return false;

  const availablePrograms = theme.availableDiscountPrograms;
  if (!availablePrograms?.length) {
    // If theme has no availableDiscountPrograms list, show loyalty if user has any enrolled programs
    return true;
  }

  const enrolledSet = new Set(enrolled.map((n) => n.toUpperCase().trim()));

  return availablePrograms.some((available) => {
    if (enrolledSet.has(available)) return true;
    if (available === ThemeAvailableDiscountProgramsEnum.CUSTOM) {
      return hasCustomPrograms(enrolled);
    }
    return false;
  });
}
